import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, CalendarClock, CheckCircle, Trash2 } from "lucide-react";
import { useViewAsClient } from "@/lib/viewAsClient";
import { toast } from "sonner";

/**
 * Dashboard widget: lists draft bookings in the next 7 days.
 * Tomorrow's drafts highlighted red. T−24h rows surface as ⚠ alert.
 * Each row: Complete (jump to BookingForm) / Delete.
 * Driven by RPC lbs_get_upcoming_drafts (migration 057).
 */
export default function UpcomingDraftsWidget() {
  const { viewAsClientId } = useViewAsClient();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.rpc("lbs_get_upcoming_drafts", {
        p_client_id: viewAsClientId || null, p_site_id: null, p_days: 7,
      });
      if (error) { if (!cancelled) { setLoading(false); console.warn(error); } return; }
      if (!cancelled) { setRows(data || []); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [viewAsClientId, tick]);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this draft booking? The bay/time slot will be released.")) return;
    const { error } = await supabase.from("lbs_bookings").delete().eq("id", id).select();
    if (error) { toast.error(error.message); return; }
    toast.success("Draft deleted.");
    setTick((k) => k + 1);
  };

  if (loading) {
    return (
      <Card data-testid="upcoming-drafts-widget">
        <CardContent className="py-6 text-sm text-slate-400">Loading upcoming drafts…</CardContent>
      </Card>
    );
  }
  if (rows.length === 0) return null; // hide when no drafts to nag about

  return (
    <Card data-testid="upcoming-drafts-widget">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <CalendarClock className="h-4 w-4" /> Upcoming drafts (next 7 days)
          <Badge variant="secondary" className="ml-1">{rows.length}</Badge>
        </CardTitle>
        <p className="text-xs text-slate-500">
          Complete each draft with driver, vehicle and load details — only then
          does the driver get their assignment email.
        </p>
      </CardHeader>
      <CardContent className="p-0">
        <ul className="divide-y">
          {rows.map((r) => {
            const start = new Date(r.scheduled_start);
            const isTomorrow = isTomorrowLocal(start);
            const urgent = (r.hours_until ?? 999) <= 24;
            return (
              <li key={r.id}
                  className={`flex items-center gap-3 px-4 py-2 text-sm ${isTomorrow ? "bg-rose-50" : ""}`}
                  data-testid={`draft-row-${r.id}`}>
                <div className="w-24 shrink-0 text-xs">
                  <div className={urgent ? "text-rose-700 font-semibold" : "text-slate-700"}>
                    {start.toLocaleDateString("en-AU", { weekday: "short", day: "2-digit", month: "short" })}
                  </div>
                  <div className="text-slate-500">{start.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", hour12: false })}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{r.site_name} {r.bay_name && <span className="text-slate-500 font-normal">· {r.bay_name}</span>}</div>
                  <div className="text-xs text-slate-500 flex flex-wrap gap-x-3">
                    <span className="capitalize">{r.direction}</span>
                    <span>Ref {r.reference_number}</span>
                    {!r.has_driver && <span className="text-rose-600">No driver</span>}
                    {!r.has_vehicle && <span className="text-rose-600">No vehicle</span>}
                  </div>
                </div>
                {urgent && (
                  <Badge variant="secondary" className="bg-rose-100 text-rose-700 gap-1">
                    <AlertTriangle className="h-3 w-3" /> &lt;24h
                  </Badge>
                )}
                <Button size="sm" variant="default" className="h-8"
                        onClick={() => navigate(`/bookings?edit=${r.id}`)}
                        data-testid={`draft-complete-${r.id}`}>
                  <CheckCircle className="h-3.5 w-3.5 mr-1" /> Complete
                </Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600"
                        onClick={() => onDelete(r.id)}
                        data-testid={`draft-delete-${r.id}`}
                        title="Delete draft">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}

function isTomorrowLocal(dt) {
  const t = new Date(); t.setDate(t.getDate() + 1); t.setHours(0,0,0,0);
  const tEnd = new Date(t); tEnd.setHours(23,59,59,999);
  const ms = dt.getTime();
  return ms >= t.getTime() && ms <= tEnd.getTime();
}
